
import {BackendInterface} from "@/scripts/BackendInterface";
import Vue, {PropType} from "vue";
import {mapState} from "vuex";
import {CalculationConfig, SolveStatus} from "@/scripts/models/calculation";
import {
  SiloAssignmentResult,
  SOLUTION_QUALITY_COLORS,
  SOLUTION_QUALITY_TRANSLATIONS,
  SolutionQuality,
  SolveProcessOutput
} from "@/scripts/models/results";
import {UserInfo, UserRole} from "@/scripts/auth";
import {CoffeeProductionModelType} from "@/scripts/models/request";
import ProgressInfo from "@/components/common/ProgressInfo.vue";
import {
  formatDateShort, formatDateSuperShort,
  formatSeconds, formatTimeShort,
  hash_java,
  isAnySuperuser,
  roundInverseMipGapValue,
  roundMipGapValue
} from "../../scripts/utils";
import SolutionQualityChip from "@/components/common/SolutionQualityChip.vue";
import {SolverOutputLineCplex} from "@/scripts/models/statistics";
import SolverStatisticsPlot from "@/components/analysis/SolverStatisticsPlot.vue";
import Gantt from "@/components/gantt/Gantt.vue";
import {
  createGanttChartConfig_MillingTask,
  createGanttChartConfig_MillingTaskSummary,
  createGanttChartConfig_RoasterTask
} from "@/scripts/gantt/coffee-gantt";
import GanttChartState from "@/scripts/gantt/GanttChartState";
import DoriRoasterTaskTable from "@/components/dori/DoriRoasterTaskTable.vue";
import computationTipsRaw from "@/data/computation-tips.json";
import UserTip from "@/components/common/UserTip.vue";

const computationTips = (computationTipsRaw ?? []) as Array<{ modelTypes: CoffeeProductionModelType[], text: string }>

export default Vue.extend({
  name: "CalculationSection",
  props: {
    solveRequest: {required: true},
    existingSolveProcessId: {type: String as PropType<string>, default: undefined},
    showSolverOutput: {type: Boolean, default: false},
    solverOutputFetchingEnabled: {type: Boolean, default: false},
    calculationConfig: {type: Object, required: true},
    modelType: {type: String as PropType<CoffeeProductionModelType>, required: true},
  },
  components: {UserTip, DoriRoasterTaskTable, Gantt, SolverStatisticsPlot, SolutionQualityChip, ProgressInfo},
  data: function () {
    return {
      SolveStatus,
      CoffeeProductionModelType,
      tab2: null,
      solverOutput: "",
      phases: [] as string[],
      solveProcessId: "",
      solution: null as SiloAssignmentResult | null,
      requestTimeoutS: 2,
      numRequests: 0,
      oldSolution: null as SiloAssignmentResult | null,
      processOutput: null as SolveProcessOutput | null,
      solveStatus: null as string | null,
      solvingInterruptedByUser: false,
      lockSolverOutputConsoleToBottom: false,
      loadUpdates: true,
      solverOutputStats: [] as SolverOutputLineCplex[],
      autoUpdateLivePreview: true,
      // DORI Live preview
      roaster_gantt_config: null as any,
      roaster_gantt_state: null as any,
      milling_gantt_config: null as any,
      milling_gantt_state: null as any,
      currentTime: Date.now(),
      calculationTab: 0,
      latestSolution: null,
      latestResultInfo: {
        hash: null as any,
        lastUpdate: null as any,
      }
    };
  },
  created() {
  },
  mounted() {
    if (this.solveRequest && !this.existingSolveProcessId) {
      this.startSolving();
    } else if (!!this.existingSolveProcessId) {
      this.solveProcessId = this.existingSolveProcessId
    }
    // check if query param with solveProcessId is set
  },
  // on before destroy, stop the interval
  beforeDestroy() {
    this.loadUpdates = false;
  },
  watch: {
    async solveProcessId() {
      const bi: BackendInterface = this.backendInterface;
      const cc: CalculationConfig = this.calculationConfig
      console.log("solveProcessId", this.solveProcessId);

      if (this.solveProcessId) {
        const timeout = async (ms: number) => new Promise((r) => setTimeout(r, ms));
        while (this.solverOutputFetchingEnabled && this.loadUpdates) {
          this.numRequests += 1;
          // console.log("solution", solution);
          // const solveStatus = await bi.sara_GetSolveStatus(this.solveProcessId);
          this.solveStatus = await cc.fetchSolveStatus(this.solveProcessId);
          // console.log("solveStatus:", this.solveStatus);
          if (this.solveStatus === SolveStatus.Scheduled) {
            // TODO: display a popup or something during notification rework
            console.log("Solving has been scheduled!");
          }
          if (this.solveStatus === SolveStatus.Solving) {
            // console.log("SOLVING");
            await this.updateSolverOutput();
            if (this.autoUpdateLivePreview) {
              await this.getLastSolution();
            }
          }
          if (this.solveStatus === SolveStatus.Finished) {
            // const solution = await bi.sara_GetSolveResultBySolveProcessId(this.solveProcessId);
            await this.updateSolverOutput();
            const solution = await cc.fetchSolveResult(this.solveProcessId);
            // console.log("FINISHED, solution:", solution);
            this.solution = solution;
            this.$emit("solution-found", this.solution?.id ?? null);
            this.loadUpdates = false
          }
          if (this.solveStatus === SolveStatus.UnknownProcessId) {
            console.warn("SolveStatus.Error");
            // TODO: display a popup or something during notification rework
            this.loadUpdates = false
          }
          if (this.solveStatus === SolveStatus.Error) {
            // TODO: display a popup or something during notification rework
            console.error("SolveStatus.Error");
            const solution = await cc.fetchSolveResult(this.solveProcessId);
            this.$emit("error", solution);
            this.loadUpdates = false
          }


          await timeout(this.requestTimeoutS * 1000);
        }
        // TODO: delete hash
        // await fetch(`${this.backendUrl}/sara/computation/delete-solver-output/${this.solveProcessId}`, {
        //   mode: "cors",
        // });
      }
    },
  },
  computed: {
    ...mapState(["backendUrl", "user", "backendInterface"]),
    isAdminOrMore(): boolean {
      if (this.modelType === CoffeeProductionModelType.SARA) {
        return this.user.saraUserRole === UserRole.ADMIN || this.user.saraUserRole === UserRole.SUPERUSER
      }
      if (this.modelType === CoffeeProductionModelType.PAPA) {
        return this.user.papaUserRole === UserRole.ADMIN || this.user.papaUserRole === UserRole.SUPERUSER
      }
      if (this.modelType === CoffeeProductionModelType.DORI) {
        return this.user.doriUserRole === UserRole.ADMIN || this.user.doriUserRole === UserRole.SUPERUSER
      }
      return false;
    },
    solutionQuality(): any {
      const solutionQualityColor = SOLUTION_QUALITY_COLORS[this.processOutput?.solutionQuality as SolutionQuality];
      const solutionQualityTranslation = SOLUTION_QUALITY_TRANSLATIONS[this.processOutput?.solutionQuality as SolutionQuality]
      return {solutionQualityColor, solutionQualityTranslation}
    },
    maxNumRequests(): number {
      return (10 * 60) / this.requestTimeoutS + 5;
    },
    phaseDict() {
      const phaseDict: { [key: string]: string[] } = {};
      for (const phase of this.phases) {
        let phaseName = phase.match(/\[Schritt [\d]+\.?[\d]*\]/)?.[0] ?? "";
        const phaseText = phase.slice(phaseName.length + 2);
        phaseName = phaseName.slice(1, -1);
        if (phaseName in phaseDict) phaseDict[phaseName].push(phaseText);
        else phaseDict[phaseName] = [phaseText];
      }
      return phaseDict;
    },
    isDori(): boolean {
      return this.modelType === CoffeeProductionModelType.DORI
    },
    isSuperuser(): boolean {
      return isAnySuperuser(this.user)
    },
    randomComputationTip(): string | null {
      if (!computationTips) return null
      const tips = computationTips.filter(c => c?.modelTypes?.includes(this.modelType))
      if (tips.length === 0) return null
      return tips[Math.floor(Math.random() * tips.length)].text
    },
  },
  methods: {
    formatSeconds,
    formatTimeShort,
    roundMipGapValue,
    roundInverseMipGapValue,
    roundObjectiveValue(value: number | "NaN" | "Infinity"): string {
      if (value === "NaN" || value === "Infinity") {
        return '-'
      }
      // use m or k as a postfix for big values
      if (value > 1_000_000) {
        return (value / 1_000_000).toFixed(1) + "m";
      } else if (value > 1_000) {
        return (value / 1_000).toFixed(1) + "k";
      } else if (value > 10) {
        return value.toFixed(1);
      } else {
        return value.toFixed(2);
      }
    },
    async updateSolverOutput() {
      const solverOutputDom = this.$refs.solverOutput as HTMLElement;

      const cc: CalculationConfig = this.calculationConfig

      // Solver output statistics
      try {
        this.solverOutputStats = await cc.fetchSolverStatistics?.(this.solveProcessId) ?? [];
      } catch (e) {
        console.warn("Error fetching solver statistics");
      }
      // console.log("solverOutputStats", this.solverOutputStats)

      // Solver Text Output
      // let resText = await this.backendInterface.sara_GetSolverOutput(this.solveProcessId);
      const processOut = await cc.fetchSolverOutput(this.solveProcessId) as SolveProcessOutput;
      this.processOutput = processOut
      // console.log("processOut", processOut)
      const resText = processOut.solutionProcessOutput;
      const scrolledToBottom =
          solverOutputDom &&
          Math.abs(solverOutputDom.scrollHeight - solverOutputDom.scrollTop - solverOutputDom.clientHeight) < 1;
      if (resText == null || resText < this.solverOutput) {
        return false;
      }
      this.phases = resText.match(/(\[Schritt [\d]+\.?[\d]*\]).*/g)?.map((r: any) => r) ?? [];
      this.solverOutput = resText;
      if (scrolledToBottom || this.lockSolverOutputConsoleToBottom) {
        setTimeout(() => (solverOutputDom.scrollTop = solverOutputDom.scrollHeight), 0);
      }
      return true;
    },
    async startSolving() {

      const cc: CalculationConfig = this.calculationConfig
      console.log("STARTING TO SOLVE");
      this.$emit("optimization-started", this.solveProcessId);
      const solveRequest = JSON.parse(JSON.stringify(this.solveRequest));
      // this.solveProcessId = await this.backendInterface.sara_Solve(solveRequest);
      this.solveProcessId = await cc.fetchSolve(solveRequest);
    },
    async interruptSolving() {
      this.solvingInterruptedByUser = true
      console.log("interrupt " + this.modelType + ": solveProcessId " + this.solveProcessId);
      await this.backendInterface.stopSolving(this.modelType, this.solveProcessId);
    },
    async getLastSolution() {
      try {
        const latestSolution = await this.calculationConfig.fetchLastSolution?.(this.solveProcessId);
        this.latestSolution = latestSolution
        if (!!this.roaster_gantt_state) {
          // this.roaster_gantt_state.initialPosX = -10000
        }
        // console.log("latestSolution", latestSolution)
        if (latestSolution != null && this.modelType === CoffeeProductionModelType.DORI) {
          this.createRoasterGantt(latestSolution)
          this.createMillingGantt(latestSolution)
          const hash = hash_java(JSON.stringify(latestSolution.result))
          if(hash !== this.latestResultInfo.hash){
            this.latestResultInfo.hash = hash
            this.latestResultInfo.lastUpdate = new Date()
          }
        }
      } catch (e) {
        console.error("Error fetching last solution", e);
      }
    },
    createRoasterGantt(latestSolution: any){
      this.roaster_gantt_config = createGanttChartConfig_RoasterTask(latestSolution)
      this.roaster_gantt_config.name = null
      const state = new GanttChartState(this.roaster_gantt_config)
      const [allStart, allEnd] = state.itemsTimespan
      const [realStart, realEnd] = state.itemsTimespanNotBlocked
      state.initialPosX = (realStart - allStart) / (allEnd - allStart);
      state.showTimeCursor = false
      state.lockTimeCursor = false
      this.roaster_gantt_state = state
    },
    createMillingGantt(latestSolution: any){
      this.milling_gantt_config = createGanttChartConfig_MillingTaskSummary(latestSolution)
      this.milling_gantt_config.name = null
      const state = new GanttChartState(this.milling_gantt_config)
      const [allStart, allEnd] = state.itemsTimespan
      const [realStart, realEnd] = state.itemsTimespanNotBlocked
      state.initialPosX = (realStart - allStart) / (allEnd - allStart);
      state.showTimeCursor = false
      state.lockTimeCursor = false
      this.milling_gantt_state = state
    }
  },
});
